/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Model } from '@app/shared/models/model.model';
import { Night } from '@app/hotels/rooms/night.model';
import { Hotel } from '@app/hotels/hotels/hotel.model';
import { HotelBooking } from '@app/hotels/hotel-bookings/hotel-booking.model';
import { Type } from '@app/settings/types/types.model';
import { SaleFinal } from '@app/sales/sales/sale-final.model';

export class Room extends Model {
  id: number;
  check_in: string; // date
  nights_count: number;
  check_out: string; // date
  min_nights_from: string;
  min_nights_to: string;
  type_id: number;
  type?: Type;
  count: number;
  meal_plan_id: number;
  meal_plan: Type;
  people: number;
  property_id: number;
  property?: Type;
  show_to_web: boolean;
  status?: string;
  hotel_stars?: number;
  hotel?: Hotel;
  budget?: string;
  nights: Array<Night>;
  extra: boolean;
  booking?: HotelBooking;
  allotment_id?: string;
  hotel_id?: number;
  ref_number: string;

  net_price_eur_avg: number;
  price_avg: number;
  margin_avg: number;
  web_price_avg: number;
  available_rooms: number;
  total_rooms: number;

  sale_final_items: Array<SaleFinal>;

  deserialize(input: any): this {
    super.deserialize(input);

    if (input.nights) {
      this.nights = input.nights.map((night: Night) => new Night().deserialize(night));
    }

    if (input.booking) {
      this.booking = new HotelBooking().deserialize(input.booking);
    }

    if (input.type) {
      this.type = new Type().deserialize(input.type);
    }

    if (input.meal_plan) {
      this.meal_plan = new Type().deserialize(input.meal_plan);
    }

    if (input.property) {
      this.property = new Type().deserialize(input.property);
    }

    return this;
  }

  /**
   * Връща средната net_price на стаята
   */
  getAvgNetPrice(): number {
    let netPrice = 0;
    this.nights.forEach((night: Night) => {
      netPrice += Number(night.net_price);
    });
    return Number((netPrice / this.nights.length).toFixed(2));
  }

  /**
   * Връща средната price на стаята
   */
  getAvgPrice(): number {
    let price = 0;
    this.nights.forEach((night: Night) => {
      price += Number(night.price);
    });
    return Number((price / this.nights.length).toFixed(2));
  }

  /**
   * Връща средната price на стаята
   */
  getAvgWebPrice(): number {
    let webPrice = 0;
    this.nights.forEach((night: Night) => {
      webPrice += Number(night.web_price);
    });
    return Number((webPrice / this.nights.length).toFixed(2));
  }

  /**
   * Връща средния margin на стаята
   */
  getAvgMargin(): number {
    let margin = 0;
    this.nights.forEach((night: Night) => {
      margin += Number(night.margin);
    });
    return Number((margin / this.nights.length).toFixed(2));
  }

  /**
   * Връща броя на available Nights
   */
  getAvailableNights(): number {
    let availableNights = 0;

    this.nights.forEach((night: Night) => {
      if (night.sold_price <= 0 && !night.hotel_booking_cancellation_id) {
        availableNights++;
      }
    });

    return availableNights;
  }

  /**
   * Връща дали има поне един available Night в стаята
   */
  isRoomAvailable(): boolean {
    let roomAvailable = false;
    this.nights.forEach((night: Night) => {
      if (night.isNightAvailable()) {
        roomAvailable = true;
      }
    });
    return roomAvailable;
  }
}
